import * as angular from 'angular';
import * as moment from 'moment';
import * as _ from 'lodash';

import { ActualDataType, ComponentDefinition, Dictionary, DigitalSet, IDateRangePickerOptions, KeyValuePair, RefreshClickData, SpotValue, TagDefinition } from '../../../models';
import { ISpotValueHelperService, ISpotValuesService, ITranslatedLoggerService } from '../../../services';
import { refreshClick } from '../../../common/events';

interface BulkSpotInputRow extends SpotValue, TagDefinition {
    digitalSet: KeyValuePair<any, string>[];
    existingSpotValue?: SpotValue;
    hasWarning: boolean;
    id: number;
    isDigital: boolean;
}

class BulkInputSpotValuesModalController implements angular.IComponentController {
    bulkForm: angular.IFormController;
    bulkInputRows: BulkSpotInputRow[];
    dateOptions: IDateRangePickerOptions;
    selectedDate: moment.Moment;
    isOpen: boolean = false;

    readonly beginDate: string;
    readonly decimals = 2;
    readonly digitalSets: Dictionary<DigitalSet>;
    readonly modalId: string;
    readonly spotTags: TagDefinition[];

    static $inject = [
        '$scope',
        '$rootScope',
        '$timeout',
        '$translate',
        'spotValueHelperService',
        'spotValuesService',
        'translatedLoggerService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: angular.IRootScopeService,
        private _$timeout: angular.ITimeoutService,
        private _$translate: angular.translate.ITranslateService,
        private _spotValueHelperService: ISpotValueHelperService,
        private _spotValuesService: ISpotValuesService,
        private _translatedLoggerService: ITranslatedLoggerService
    ) {
    }

    $onInit() {
        this._$scope.$watch(() => this.selectedDate, (newValue, oldValue) => {
            if (newValue?.isSame && !newValue.isSame(oldValue) && this.bulkInputRows?.length) {
                this.checkForExistingValues();
            }
        }, true);
        this.resetDate();
    }

    $onChanges(changes: angular.IOnChangesObject) {
        if (changes['spotTags']?.currentValue) {
            this.mapRows(this.spotTags);
        }
        if (changes['beginDate']) {
            this.resetDate();
        }
    }

    onSave(): void {
        this.bulkInputRows.forEach(row => row.timestamp = this.selectedDate.toISOString());
        this._spotValuesService
            .createOrUpdateMany(this.bulkInputRows)
            .then(() => {
                this._translatedLoggerService.logSuccess('Saved');
                this._$rootScope.$broadcast(refreshClick, { force: true } as RefreshClickData);
                this.resetRows();
            });
    }

    onOpen() {
        this.isOpen = true;
        this._$timeout(() => {
            this.checkForExistingValues();
        });
    }

    onClose(): void {
        this.isOpen = false;
        this._$timeout(() => {
            this.resetRows();
        });
    }

    deleteBulkRow(rowId: number): void {
        _.remove(this.bulkInputRows, r => r.id === rowId);
    }

    getWarningTitle(row: BulkSpotInputRow): string {
        if (row.existingSpotValue) {
            let spotValueAndComment = '';
            if (row.isDigital) {
                const digitalValue = row.digitalSet.find(kvp => kvp.key === row.existingSpotValue.value);
                if (digitalValue) {
                    spotValueAndComment = `${digitalValue.value}`;
                }
            } else {
                spotValueAndComment = `${row.existingSpotValue.value}`;
            }
            if (row.existingSpotValue.comment) {
                spotValueAndComment = spotValueAndComment + ' / ' + row.existingSpotValue.comment;
            }
            return this._$translate.instant('Delete this row to keep existing value (_spotValue_)', { spotValue: spotValueAndComment });
        }
    }

    // 40258 : need to set the date in the picker AND synchronize with the calendar.
    // The hack below triggers an event in the underlying control to refresh the calendar and bypass a bug.
    // Don't ask anymore questions please.
    private resetDate() {
        const dateAsString = this.beginDate ?? new Date().toISOString();
        this.dateOptions = null;
        this.selectedDate = { startDate: moment(dateAsString) } as any;
        this._$timeout(() => {
            this.selectedDate = moment(dateAsString);
            this.dateOptions = this._spotValueHelperService.getDateOptions();
        });
    }

    private checkForExistingValues(): void {
        if (this.bulkInputRows?.length && this.selectedDate) {
            const tagGuids = this.bulkInputRows.map(r => r.tagGuid);
            this._spotValuesService
                .getAtDateForList(tagGuids, this.selectedDate.toISOString(), true)
                .then(results => {
                    this.bulkInputRows.forEach(item => {
                        item.hasWarning = (!!results[item.tagGuid] && !!results[item.tagGuid][0]);
                        item.existingSpotValue = (!!results[item.tagGuid] && !!results[item.tagGuid][0])
                            ? results[item.tagGuid][0]
                            : undefined;
                    });
                })
                .catch(error => this._translatedLoggerService.logError(error));
        }
    }

    private resetRows(): void {
        this.bulkInputRows = [];
        this.resetDate();
        this.mapRows(this.spotTags);
    }

    private mapRows(spotTags: TagDefinition[]): void {
        this.bulkInputRows = spotTags.map((spotTag, index) => {
            const item = Object.assign({}, spotTag as BulkSpotInputRow);
            item.id = index + 1;
            item.isDigital = spotTag.actualDataType === ActualDataType.Digital;
            item.digitalSet = this.getMatchingDigitalSet(spotTag);
            item.tagGuid = spotTag.guid;
            item.hasWarning = false;
            return item;
        });
    }

    private getMatchingDigitalSet(spotTag: TagDefinition): KeyValuePair<any, string>[] {
        let digitalSet: KeyValuePair<any, string>[] = [];
        if (spotTag.actualDataType === ActualDataType.Digital) {
            const foundDigitalSet = _.find(this.digitalSets, ds => ds.guid === spotTag.digitalSetGuid);
            if (foundDigitalSet) {
                digitalSet = foundDigitalSet.stateCollection.map(state => {
                    return { key: state.code, value: state.name };
                });
            }
        }
        return digitalSet;
    }
}

export const wtBulkInputSpotValuesModal: ComponentDefinition = {
    componentId: 'wtBulkInputSpotValuesModal',
    controller: BulkInputSpotValuesModalController,
    template: require('./bulkInputSpotValuesModal.component.html'),
    controllerAs: 'vm',
    bindings: {
        beginDate: '<',
        digitalSets: '<',
        modalId: '@',
        spotTags: '<'
    }
};